import React, { useState } from "react";
import { Card } from "antd";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
  Marker,
} from "react-simple-maps";
import { currency, US_STATES_GEOJSON } from "../utils/utils";
import { fuzzyMatchLocation } from "../utils/locationMatching";

/* -------------------- Types -------------------- */ 

export interface MarketDataNew {
  location: string;
  Investment: string;
  EstimatedInvestment: string;
  duration: string;
  Power: number;
  abs_lift_in_perc: string;
  PercentInvestment: string;
  isState?: boolean;
  coordinated?: [number, number];
}

interface HoverData extends MarketDataNew {
  x: number;
  y: number;
  type: "state" | "city";
}

interface MarketAnalysisDashboardProps {
  data: any[];
  allLocationData: any[];
  testMarkets?: string[];
  selectedMarket?: string;
}

/* A location string from the API ("New York, NY") and a state/geography
   name from the map ("New York") often differ in formatting, so match
   loosely in both directions rather than requiring an exact match. Reuses
   the same fuzzy matcher PreCampaign.tsx uses for the identical problem
   (matching location strings across differently-formatted API responses). */
const locationMatches = (a?: string, b?: string) => {
  if (!a || !b) return false;
  return fuzzyMatchLocation(a, b);
};

/* -------------------- Helpers -------------------- */

// Must stay in sync with the tooltip's actual rendered size (w-[300px]
// below) — used to keep it inside the viewport when clamping position.
const TOOLTIP_WIDTH = 300;
const TOOLTIP_HEIGHT = 280;

const parseInvestment = (value: string) =>
  Number(value?.replace(/[^0-9.]/g, "")) || 0;

/* -------------------- Component -------------------- */

const MarketAnalysisDashboard: React.FC<MarketAnalysisDashboardProps> = ({
  data,
  allLocationData = [],
  testMarkets = [],
  selectedMarket,
}) => {
  const [hovered, setHovered] = useState<HoverData | null>(null);

  /* ---------- Hover Handlers ---------- */

  const handleHover = (
    e: React.MouseEvent<SVGElement, MouseEvent>,
    item: MarketDataNew,
    type: "state" | "city"
  ) => {
    setHovered({
      ...item,
      type,
      x: e.clientX,
      y: e.clientY,
    });
  };

  /* ---------- Transform Data ---------- */

  const transformedData: MarketDataNew[] = data.map((item) => {
    const match = allLocationData.find((coords) =>
      coords.locationOriginal
        ?.toLowerCase()
        .includes(item.location.toLowerCase())
    );

    return {
      ...item,
      coordinated: match ? [match.latitude, match.longitude] : undefined,
    };
  });

  const stateData = transformedData.filter((d) => d.isState);
  const cityData = transformedData.filter((d) => !d.isState);

  /* -------------------- Render -------------------- */

  return (
    <div className="flex flex-col gap-4 w-full h-full mt-5">
      <Card
        className="flex-1 shadow-xl rounded-lg"
        bodyStyle={{
          position: "relative",
          overflow: "visible",
        }}
      >
        <ComposableMap
          projection="geoAlbersUsa"
          width={980}
          height={600}
          style={{ width: "100%", height: "auto" }}
        >
          <ZoomableGroup>
            {/* ---------- STATES ---------- */}
            <Geographies geography={US_STATES_GEOJSON}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const stateName = geo.properties.name;

                  const state = stateData.find(
                    (s) => s.location.toLowerCase() === stateName.toLowerCase()
                  );

                  const investment = state
                    ? parseInvestment(state.Investment)
                    : 0;

                  // Investment is 0 for demo tenants / studies without ad-spend
                  // data (see backend marketSelectionSingleSCM), which would
                  // otherwise render every state in the same flat color. Fall
                  // back to statistical Power (0-100) so the map still shows a
                  // meaningful highlight gradient in that case.
                  const power = state ? Number(state.Power) || 0 : 0;
                  const useInvestment = investment > 0;
                  const metric = useInvestment ? investment : power;

                  const fillColor = !state
                    ? "var(--bg-hover)"
                    : useInvestment
                    ? metric > 300
                      ? "#1e3a8a"
                      : metric > 200
                      ? "#1d4ed8"
                      : metric > 100
                      ? "#3b82f6"
                      : "#93c5fd"
                    : metric > 80
                    ? "#1e3a8a"
                    : metric > 60
                    ? "#1d4ed8"
                    : metric > 40
                    ? "#3b82f6"
                    : metric > 0
                    ? "#93c5fd"
                    : "var(--bg-hover)";

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={fillColor}
                      stroke="var(--border-default)"
                      strokeWidth={0.6}
                      onMouseEnter={(e) =>
                        state && handleHover(e, state, "state")
                      }
                      onMouseLeave={() => setHovered(null)}
                      style={{
                        default: { outline: "none" },
                        hover: {
                          outline: "none",
                          filter: "brightness(0.95)",
                          cursor: state ? "pointer" : "default",
                        },
                        pressed: { outline: "none" },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {/* ---------- CITIES ---------- */}
            {cityData.map((city) => {
              if (!city.coordinated) return null;
              const [lat, lng] = city.coordinated;

              const isSelected = locationMatches(selectedMarket, city.location);
              const isTestMarket =
                !isSelected &&
                testMarkets.some((tm) => locationMatches(tm, city.location));

              const fill = isSelected
                ? "var(--success)"
                : isTestMarket
                ? "var(--blue)"
                : "#16a34a";
              const radius = isSelected ? 13 : isTestMarket ? 12 : 10;

              return (
                <Marker key={city.location} coordinates={[lng, lat]}>
                  <circle
                    r={radius}
                    fill={fill}
                    stroke="#ffffff"
                    strokeWidth={2}
                    onMouseEnter={(e) => handleHover(e, city, "city")}
                    onMouseLeave={() => setHovered(null)}
                  />
                </Marker>
              );
            })}
          </ZoomableGroup>
        </ComposableMap>

        {/* ---------- Tooltip (State + City) ---------- */}
        {hovered && (
          <div
            style={{
              position: "fixed",
              // Clamp to the viewport so the card doesn't get clipped when
              // hovering markers/states near the right or bottom edge.
              top: Math.max(12, Math.min(hovered.y + 12, window.innerHeight - TOOLTIP_HEIGHT - 12)),
              left: Math.max(12, Math.min(hovered.x + 12, window.innerWidth - TOOLTIP_WIDTH - 12)),
              zIndex: 9999,
              background: "var(--bg-elevated)",
              border: "1px solid var(--border-default)",
              color: "var(--text-primary)",
            }}
            className="rounded-2xl shadow-xl w-[300px]"
          >
            <div className="p-4" style={{ background: "var(--blue-subtle)", borderBottom: "1px solid var(--border-subtle)", borderTopLeftRadius: 16, borderTopRightRadius: 16 }}>
              <h3 className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>
                {hovered.location}
              </h3>
              <div className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                {hovered.type === "state" ? "State Overview" : "City Overview"}
              </div>
            </div>

            <div className="p-4 space-y-3 text-sm">
              {/* <div className="flex justify-between">
                <span>💵 Investment</span>
                <strong>{currency(parseInvestment(hovered.Investment))}</strong>
              </div> */}
              {/* <div className="flex justify-between">
                <span>💵 Estimated Investment</span>
                <strong>
                  {currency(parseInvestment(hovered.EstimatedInvestment))}
                </strong>
              </div> */}

              {/* <div className="flex justify-between">
                <span>⏱ Duration</span>
                <strong>{hovered.duration}</strong>
              </div> */}

              <div className="flex justify-between">
                <span>⚡ Power</span>
                <strong>{hovered.Power}</strong>
              </div>

              <div className="flex justify-between">
                <span>📈 Abs Lift</span>
                <strong style={{ color: "var(--success)" }}>
                  +{hovered.abs_lift_in_perc}
                </strong>
              </div>

              <div className="flex justify-between">
                <span>📊 % Investment</span>
                <strong>{hovered.PercentInvestment}</strong>
              </div>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MarketAnalysisDashboard;
